import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getTasksQueue } from "@/lib/operations.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { ScopeBar } from "@/components/scope-bar";
import { HelpButton } from "@/components/kb/help-button";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { EmptyState } from "@/components/ui/empty-state";
import { LoadingState } from "@/components/ui/loading-state";
import {
  ClipboardList, Truck, ClipboardCheck, ChevronRight, Clock, AlertTriangle, PackageOpen, HandMetal,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/tasks")({ component: TasksPage });

function ageHours(iso?: string | null) {
  if (!iso) return 0;
  return Math.max(0, (Date.now() - new Date(iso).getTime()) / 36e5);
}
function urgencyTone(hours: number): "neutral" | "info" | "warning" | "danger" {
  if (hours >= 24) return "danger";
  if (hours >= 8) return "warning";
  if (hours >= 2) return "info";
  return "neutral";
}
function ageLabel(h: number) {
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

function TaskRow({
  to, params, primary, secondary, icon: Icon, age, status,
}: {
  to: string;
  params?: Record<string, string>;
  primary: string;
  secondary?: string;
  icon: React.ComponentType<{ className?: string }>;
  age: number;
  status?: string;
}) {
  const navigate = useNavigate();
  const tone = urgencyTone(age);
  return (
    <button
      type="button"
      onClick={() => navigate({ to: to as any, params: params as any })}
      className="w-full flex items-center gap-3 px-3 py-3 hover:bg-accent/30 active:bg-accent/50 text-left min-h-[var(--tap-md,44px)] border-b last:border-b-0"
    >
      <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium font-mono text-sm truncate">{primary}</div>
        {secondary && <div className="text-xs text-muted-foreground truncate">{secondary}</div>}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {status && <StatusPill tone={statusTone(status)}>{status}</StatusPill>}
        <StatusPill tone={tone} dot={tone !== "neutral"}>
          <Clock className="h-3 w-3" /> {ageLabel(age)}
        </StatusPill>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </button>
  );
}

function TaskGroup({
  title, count, icon: Icon, children, loading, empty,
}: {
  title: string; count: number; icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode; loading?: boolean; empty?: string;
}) {
  return (
    <section className="rounded-lg border bg-card overflow-hidden">
      <header className="flex items-center justify-between px-4 py-3 border-b bg-muted/40">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        </div>
        <StatusPill tone={count === 0 ? "neutral" : count > 10 ? "warning" : "info"}>{count}</StatusPill>
      </header>
      <div>
        {loading ? <div className="p-3"><LoadingState rows={3} /></div>
        : count === 0 ? <div className="p-4 sm:p-6"><p className="text-sm text-muted-foreground text-center">{empty ?? "Nothing here."}</p></div>
        : children}
      </div>
    </section>
  );
}

function TasksPage() {
  const { tenantId, facilityId, customerId, customers } = useScope();
  const cName = (id: string) => customers.find((c) => c.id === id)?.name ?? "—";
  const fetchQueue = useServerFn(getTasksQueue);

  const { data: queue, isLoading } = useQuery({
    queryKey: ["task-queue", tenantId, facilityId, customerId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => fetchQueue({ data: { tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null } }),
  });
  const orders = queue?.orders ?? [];
  const asns = queue?.asns ?? [];
  const counts = queue?.counts ?? [];
  const ordersLoading = isLoading;
  const asnsLoading = isLoading;
  const countsLoading = isLoading;

  const toAllocate = orders.filter((o) => o.status === "open");
  const toPick = orders.filter((o) => o.status === "allocated");
  const toShip = orders.filter((o) => o.status === "picked");

  const total = toAllocate.length + toPick.length + toShip.length + asns.length + counts.length;
  const overdue = orders.filter((o) => ageHours(o.created_at) >= 24).length;

  return (
    <>
      <PageHeader
        title="Tasks"
        description="Live work queue across the floor — sorted by age."
        meta={
          <>
            <StatusPill tone={total === 0 ? "success" : "info"} dot>{total} open</StatusPill>
            {overdue > 0 && <StatusPill tone="danger" dot><AlertTriangle className="h-3 w-3" /> {overdue} overdue</StatusPill>}
          </>
        }
        actions={<><ScopeBar /><HelpButton moduleKey="tasks" /></>}
      />

      <div className="px-4 sm:px-6 py-4 space-y-4">
        {total === 0 && !ordersLoading && !asnsLoading && !countsLoading && (
          <EmptyState
            icon={ClipboardCheck}
            title="All Caught Up"
            description="No outstanding work. Time for a coffee — or start a cycle count."
          />
        )}

        <div className="grid gap-4 lg:grid-cols-3">
          <TaskGroup title="To Allocate" count={toAllocate.length} icon={ClipboardList} loading={ordersLoading} empty="No new orders.">
            {toAllocate.map((o) => (
              <TaskRow key={o.id} to="/app/orders/$orderId" params={{ orderId: o.id }}
                icon={ClipboardList} primary={o.order_number} secondary={cName(o.customer_id)}
                age={ageHours(o.created_at)} status={o.status} />
            ))}
          </TaskGroup>

          <TaskGroup title="To Pick" count={toPick.length} icon={HandMetal} loading={ordersLoading} empty="Nothing waiting on picks.">
            {toPick.map((o) => (
              <TaskRow key={o.id} to="/app/orders/$orderId" params={{ orderId: o.id }}
                icon={HandMetal} primary={o.order_number} secondary={cName(o.customer_id)}
                age={ageHours(o.created_at)} status={o.status} />
            ))}
          </TaskGroup>

          <TaskGroup title="To Pack" count={toShip.length} icon={PackageOpen} loading={ordersLoading} empty="No picked orders awaiting pack.">
            {toShip.map((o) => (
              <TaskRow key={o.id} to="/app/pack/$orderId" params={{ orderId: o.id }}
                icon={PackageOpen} primary={o.order_number} secondary={cName(o.customer_id)}
                age={ageHours(o.created_at)} status={o.status} />
            ))}
          </TaskGroup>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <TaskGroup title="Inbound" count={asns.length} icon={Truck} loading={asnsLoading} empty="No inbound work.">
            {asns.map((a) => (
              <TaskRow key={a.id} to="/app/receiving/$asnId" params={{ asnId: a.id }}
                icon={Truck} primary={a.reference} secondary={cName(a.customer_id)}
                age={ageHours(a.expected_at ?? a.created_at)} status={a.status} />
            ))}
          </TaskGroup>

          <TaskGroup title="Cycle Counts" count={counts.length} icon={ClipboardCheck} loading={countsLoading} empty="No counts pending.">
            {counts.map((c) => (
              <TaskRow key={c.id} to="/app/cycle-counts/$id" params={{ id: c.id }}
                icon={ClipboardCheck} primary={c.name} age={ageHours(c.created_at)} status={c.status} />
            ))}
          </TaskGroup>
        </div>
      </div>
    </>
  );
}
